import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { Box } from '@mui/material'
import request from 'api'
import { Loading } from 'components/ui'
import { getGlobalConsentApi } from 'pages/policies/api'
import { Consent, appendLegacyParams, buildPolicySearch } from 'pages/policies/utils'
import { convertCountryCode2to3, countryCode } from 'utils/i18n'
import i18n from 'utils/i18n'

import Detail from '../views/detail'

export default function PrivacyPolicies() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const paramCountryCode = searchParams.get('countryCode') || undefined
  const paramLocale = searchParams.get('locale') || undefined
  const effectiveCountryCode = paramCountryCode ? convertCountryCode2to3(paramCountryCode) : countryCode
  const policySearch = buildPolicySearch(searchParams)

  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [terms, setTerms] = useState<Consent>()
  const [detail, setDetail] = useState<string>('')

  const getDetailApi = (uri: string) => {
    const LEGACY_HOST = 'https://account.samsung.com'
    return request
      .get(uri)
      .then((res) => {
        if (res.status !== 200) {
          throw new Error('Failed getConsentBody')
        }

        const document = new DOMParser().parseFromString(res.data, 'text/html')

        const language = paramLocale?.replace('-', '_') || i18n.language?.replace('-', '_')
        if (language) {
          document.querySelectorAll('a').forEach((tag) => {
            if (tag.href.includes(`${LEGACY_HOST}`)) {
              tag.href = appendLegacyParams(tag.href, language, paramCountryCode)
            }
          })
        }

        const body = document.getElementsByTagName('body').item(0)?.innerHTML
        if (body !== undefined) {
          const sanitized = body.replace(
            'body { word-wrap:break-word; word-break:keep-all; padding:1.5em; }',
            'body { word-wrap:break-word; word-break:keep-all; }'
          )
          setDetail(sanitized)
        }
      })
      .catch(() => setDetail('<div>Failed to load</div>'))
  }

  useEffect(() => {
    if (effectiveCountryCode === 'KOR') {
      navigate(`/policies/privacy-notices/latest${policySearch}`)
    } else {
      getGlobalConsentApi('SEC_PP', false, {
        appId: 'abcd123456',
        overrides: { countryCode: paramCountryCode, locale: paramLocale }
      })
        .then((res) => {
          if (res.status !== 200 || !res.data[0].uri) {
            throw new Error('Failed getConsentApi')
          }

          const data = res.data[0] as Consent
          setTerms(data)
          return data
        })
        .then((data) => {
          return getDetailApi(data.uri)
        })
        .catch(() => {
          setTerms(undefined)
        })
        .finally(() => {
          setIsLoading(false)
        })
    }
  }, [paramCountryCode, paramLocale])

  return (
    <Box sx={{ width: '100%', height: '100%', padding: { xs: '24px', lg: '0' }, paddingTop: { xs: '5px', lg: '46px' } }}>
      {isLoading ? (
        <Loading fullScreen />
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Box sx={{ width: { xs: '100%', lg: '1040px' } }}>
            <Detail title={terms?.title || ''} isLoading={isLoading} detail={detail} />
          </Box>
        </Box>
      )}
    </Box>
  )
}
